import { useCheckout } from "./shared";
import "./CheckoutForm.css";

/**
 * The real checkout form + order summary, shared by all 17 CheckoutTemplate{N}.
 * Structure reuses each template's own class prefix (`${p}-grid`, `${p}-card`,
 * `${p}-field`, `${p}-sum`, `${p}-row`, `${p}-total`, `${p}-pay`) so it inherits
 * that template's styling for free; the new bits (plan / gateway / coupon /
 * errors) use theme-driven `co-*` classes from CheckoutForm.css.
 *
 * Props: `data` (fetched checkout payload), `code` (course code), `p` (class
 * prefix, e.g. "cok1"), `c` (resolved copy from shared.copy()).
 */
export default function CheckoutForm({ data = {}, code, p, c }) {
  const co = useCheckout(code, data);
  const {
    fields, plans, gateways, form, set, selectPlan, selectGateway, selectedPlan,
    bumps, selectedBumps, toggleBump,
    coupon, checking, applyCoupon, baseAmount, payable, isFree, submit, submitting,
    error, fieldErrors, placeholder, fmt, trust, accepted, setAccepted,
  } = co;

  const showPlans = plans.length > 1;
  const showGateways = !isFree && gateways.length > 0;
  const chosenBumps = bumps.filter((b) => selectedBumps.includes(b.bump_product_id));

  return (
    <form className={`${p}-grid`} onSubmit={submit} noValidate>
      {/* ── Buyer details + plan + payment method ──────────────────── */}
      <div className={`${p}-card`}>
        <h3 className={`${p}-ch`}>Your details</h3>

        {fields.map((f) => (
          <label key={f.name} className={`${p}-field`}>
            <span>
              {f.label}
              {f.required ? <em className="co-req"> *</em> : null}
            </span>
            <input
              type={f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"}
              value={form[f.name] ?? ""}
              onChange={set(f.name)}
              placeholder={placeholder(f.name)}
              aria-invalid={!!fieldErrors[f.name]}
            />
            {fieldErrors[f.name] ? <small className="co-err">{fieldErrors[f.name]}</small> : null}
          </label>
        ))}

        {showPlans ? (
          <>
            <h3 className={`${p}-ch ${p}-ch2`}>Choose your plan</h3>
            <div className="co-plans">
              {plans.map((pl) => {
                const on = Number(form.pricing_id) === pl.pricing_id;
                return (
                  <button
                    type="button"
                    key={pl.pricing_id}
                    className={`co-plan${on ? " co-plan-on" : ""}`}
                    onClick={() => selectPlan(pl.pricing_id)}
                  >
                    <span className="co-plan-name">{pl.name}</span>
                    <span className="co-plan-price">
                      {fmt(Number(pl.price) + Number(pl.setup_fee || 0))}
                      {pl.type === "subscription" && pl.frequency ? <em> / {pl.frequency}</em> : null}
                    </span>
                  </button>
                );
              })}
            </div>
            {fieldErrors.pricing_id ? <small className="co-err">{fieldErrors.pricing_id}</small> : null}
          </>
        ) : null}

        {showGateways ? (
          <>
            <h3 className={`${p}-ch ${p}-ch2`}>Payment method</h3>
            <div className="co-gateways">
              {gateways.map((g) => {
                const on = Number(form.integration_id) === g.integration_id;
                return (
                  <button
                    type="button"
                    key={g.integration_id}
                    className={`co-gw${on ? " co-gw-on" : ""}`}
                    onClick={() => selectGateway(g.integration_id)}
                  >
                    {g.image ? <img src={g.image} alt="" className="co-gw-img" /> : null}
                    <span>{g.label}</span>
                  </button>
                );
              })}
            </div>
            {fieldErrors.integration_id ? <small className="co-err">{fieldErrors.integration_id}</small> : null}
          </>
        ) : null}

        {bumps.length ? (
          <>
            <h3 className={`${p}-ch ${p}-ch2`}>Add to your order</h3>
            <div className="co-bumps">
              {bumps.map((b) => {
                const on = selectedBumps.includes(b.bump_product_id);
                const d = b.data || {};
                return (
                  <label key={b.bump_product_id} className={`co-bump${on ? " co-bump-on" : ""}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleBump(b.bump_product_id)} />
                    <span className="co-bump-body">
                      <span className="co-bump-head">
                        <strong>{d.headcontent || b.bump_name}</strong>
                        <em>+{fmt(b.bump_price)}</em>
                      </span>
                      {d.subheadcontent || d.bumpdescontent ? (
                        <span className="co-bump-desc">{d.subheadcontent || d.bumpdescontent}</span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        ) : null}
      </div>

      {/* ── Order summary ──────────────────────────────────────────── */}
      <aside className={`${p}-card ${p}-sum`}>
        <h3 className={`${p}-ch`}>Order summary</h3>

        <div className={`${p}-row`}>
          <span>{selectedPlan?.name || c.label}</span>
          <strong>{fmt(baseAmount)}</strong>
        </div>

        {chosenBumps.map((b) => (
          <div key={b.bump_product_id} className={`${p}-row`}>
            <span>{(b.data && b.data.headcontent) || b.bump_name}</span>
            <strong>{fmt(b.bump_price)}</strong>
          </div>
        ))}

        {data.coupon_enabled ? (
          <div className="co-coupon">
            <input
              value={form.coupon ?? ""}
              onChange={set("coupon")}
              placeholder="Discount / referral code"
              className="co-coupon-in"
            />
            <button type="button" className="co-coupon-btn" onClick={applyCoupon} disabled={checking || !form.coupon}>
              {checking ? "…" : "Apply"}
            </button>
          </div>
        ) : null}

        {coupon.message ? (
          <p className={`co-coupon-msg${coupon.status ? " co-coupon-ok" : coupon.status === false ? " co-coupon-bad" : ""}`}>
            {coupon.message}
          </p>
        ) : null}

        {coupon.status ? (
          <div className={`${p}-row`}>
            <span>Discount</span>
            <strong>-{fmt(coupon.discount)}</strong>
          </div>
        ) : null}

        <div className={`${p}-row ${p}-total`}>
          <span>Total due</span>
          <span>{isFree ? "Free" : fmt(payable)}</span>
        </div>

        {error ? <p className="co-form-err">{error}</p> : null}

        {trust.terms_show ? (
          <label className="co-terms">
            <input type="checkbox" checked={!!accepted} onChange={(e) => setAccepted(e.target.checked)} />
            <span>
              {trust.terms_label || "I agree to the Terms & Conditions"}
              {trust.terms_url ? (
                <>
                  {" "}
                  <a href={trust.terms_url} target="_blank" rel="noreferrer">
                    (view)
                  </a>
                </>
              ) : null}
            </span>
          </label>
        ) : null}
        {fieldErrors.terms ? <small className="co-err">{fieldErrors.terms}</small> : null}

        <button className={`co-pay ${p}-pay`} type="submit" disabled={submitting}>
          {submitting ? "Processing…" : isFree ? "Get instant access" : `Pay ${fmt(payable)}`}
        </button>

        {trust.guarantee_show && (trust.guarantee_title || trust.guarantee_body) ? (
          <div className="co-guarantee">
            <span className="co-guarantee-badge">✓</span>
            <div>
              {trust.guarantee_title ? <strong>{trust.guarantee_title}</strong> : null}
              {trust.guarantee_body ? <p>{trust.guarantee_body}</p> : null}
            </div>
          </div>
        ) : null}

        <p className={`${p}-note`}>🔒 {trust.secure_text || "256-bit SSL encrypted · your details are secure"}</p>
      </aside>
    </form>
  );
}
